import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { GlobalState } from "../../store"
import { fetchFormattingSettings } from "../common/userFormattingSettings/slice"

import { fieldsAPI, itemDetailAPI } from "../../apis"
import { setFields } from "../fields/slice"
import { IActionsData, IItem, IItemDetail, IItemDetailState } from "."
import {
    fetchFlowControlButtons,
    panelFooterReducersBuilder,
} from "./components/panelFooter/thunks"
import { addErrorNotification } from "../notifications/actions"
import { PageFooter } from "./components/pageFooter"
import { PanelFooter } from "./components/panelFooter"
import { PageHeader } from "./components/pageHeader/pageHeader"
import { IUserFormattingSettings } from "../applicationheader/components/usermenu"
import { IButton } from "../common/contextmenu"

export const itemDetailSlice = createSlice({
    name: "fields",

    initialState: {
        item: {} as IItem,
        pageHeader: {} as PageHeader,
        pageFooter: {} as PageFooter,
        metaData: [],
        feedbackKeys: {},
        isInitialized: false,
        isLoading: false,
        itemId: -1,
        tabs: [],
        actions: {} as IActionsData,
        panelFooter: {} as PanelFooter,
        buttons: [],
    } as IItemDetailState,

    reducers: {
        setItemId: (state, action: PayloadAction<number>) => {
            state.item.id = action.payload
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchItemDetail.fulfilled, (state, action) => {
                state.isLoading = false
                state.isInitialized = true
                state.metaData = action.payload.metaData
                state.pageFooter = action.payload.footer
                state.pageHeader = action.payload.header
                state.item = action.payload.item
                state.tabs = action.payload.tabs
            })
            .addCase(fetchActionButtons.fulfilled, (state, action) => {
                state.buttons = action.payload
            })
            .addCase(fetchItemDetail.pending, (state) => {
                state.isLoading = true
            })
            .addCase(fetchItemDetail.rejected, (state) => {
                state.isLoading = false
            })

        panelFooterReducersBuilder(builder)
    },
})

export const { setItemId } = itemDetailSlice.actions

export default itemDetailSlice.reducer

export const defaultRequestingFieldsErrorMessage =
    "Something went wrong when fetching the itemDetail."

export const fetchItemDetail = createAsyncThunk(
    "itemDetail/fetchItemDetailByItemId",
    async (itemId: number, { getState, dispatch }) => {
        const state = getState() as GlobalState

        let userFormattingSettings = state.userFormattingSettings

        if (!userFormattingSettings.isInitialized) {
            const response = await dispatch(fetchFormattingSettings())
            userFormattingSettings = response.payload as IUserFormattingSettings
        }

        let itemDetail: IItemDetail = {} as IItemDetail

        try {
            itemDetail = await itemDetailAPI.getItemDetail(
                itemId,
                userFormattingSettings,
                { $expand: "item($expand=concept,status)" },
            )
            const fields = itemDetail.tabs.flatMap((tab) => tab.fields)
            dispatch(setFields(fields))
            dispatch(fetchFlowControlButtons(itemId))
        } catch (error) {
            console.error(error)
            dispatch(addErrorNotification(defaultRequestingFieldsErrorMessage))
        }

        return itemDetail
    },
    {
        condition: (_, { getState }) => {
            const { itemDetail } = getState() as GlobalState
            if (itemDetail.item.id === -1 || itemDetail.isLoading) {
                return false
            }
        },
    },
)

export const fetchActionButtons = createAsyncThunk(
    "itemDetail/fetchActionButtons",
    async (_, { getState, dispatch }) => {
        const state = getState() as GlobalState
        let buttons: IButton[] = []

        try {
            buttons = await fieldsAPI.collectItemActions(
                state.itemDetail.item.id,
            )
        } catch (error) {
            console.error(error)
            dispatch(addErrorNotification(defaultRequestingFieldsErrorMessage))
        }

        return buttons
    },
)
